import { useState } from "react";
import type { MtbTrailRelatedPlace, RelatedPlace } from "~/lib/api";
import { toast } from "~/components/ui/toaster";
import { useEditorMode } from "~/lib/editorModeStore";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icons/icon";
import { PanelSection } from "~/components/ui/panel-section";
import { TrailListItem } from "./TrailListItem";

interface TrailsSectionProps {
  placeId: string;
  trails: MtbTrailRelatedPlace[];
  canEdit?: boolean;
  onRemove?: (relatedPlaceId: string) => Promise<void>;
  onSelect?: (place: RelatedPlace) => void;
}

export function TrailsSection({ placeId, trails, canEdit, onRemove, onSelect }: TrailsSectionProps) {
  const startLinking = useEditorMode((s) => s.startLinking);
  const [removingId, setRemovingId] = useState<string | null>(null);

  if (!canEdit && trails.length === 0) return null;

  function handleAdd() {
    startLinking({
      sourcePlaceId: placeId,
      allowedTypes: ["mtb-trail"],
      categoryLabel: "Trails",
    });
  }

  async function handleRemove(relatedPlaceId: string) {
    if (!onRemove) return;
    setRemovingId(relatedPlaceId);
    try {
      await onRemove(relatedPlaceId);
    } catch {
      toast.error("Failed to remove trail");
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <>
      <PanelSection.Separator />
      <section aria-label="Trails">
        <PanelSection.Root>
          <PanelSection.Title>
            <span className="flex items-center gap-1.5">
              <Icon i="mtb-trail" className="size-3.5" />
              Trails
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-auto px-1 py-0 text-xs"
                  onClick={handleAdd}
                  aria-label="Add trails"
                >
                  + Add
                </Button>
              )}
            </span>
          </PanelSection.Title>
          <PanelSection.Content>
            {trails.length === 0 ? (
              <button
                type="button"
                onClick={handleAdd}
                aria-label="Add trails"
                className="w-full rounded-md border border-dashed border-muted-foreground/25 px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:border-muted-foreground/50"
              >
                Add trails to this bike park
              </button>
            ) : (
              <div className="flex flex-col -mx-2">
                {trails.map((trail) => (
                  <TrailListItem
                    key={trail.id}
                    place={trail}
                    onRemove={canEdit ? () => handleRemove(trail.id) : undefined}
                    onSelect={onSelect ? () => onSelect(trail) : undefined}
                    disabled={removingId === trail.id}
                  />
                ))}
              </div>
            )}
          </PanelSection.Content>
        </PanelSection.Root>
      </section>
    </>
  );
}
